import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Card,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  BookmarkBorder,
  ChevronRight,
  Close,
  CreditCard,
  FavoriteBorder,
  Home,
  Logout,
  Menu,
  NotificationsOutlined,
  PersonOutline,
  Search,
  Settings,
  Visibility,
  Wallet,
  VerifiedUser,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import RotatingProfileCard from '../components/RotatingProfileCard';
import HeaderProfileMenu from '../components/HeaderProfileMenu';
import '../styles/maandvo-dashboard.css';

const MAROON = '#7b0011';
const OPEN_COMPLETE_PROFILE_ONCE_KEY = 'maandvo_open_complete_profile_once';

interface DashboardSummary {
  wallet_balance: number;
  total_spent: number;
  total_earned: number;
  profile_views: number;
  connections_count: number;
  recent_recharges?: Array<{ id: number; amount: number; status: string; created_at: string }>;
  recent_spends?: Array<{ id: number; amount_inr: number; description?: string; target_profile_id?: string; transaction_type: string; created_at: string }>;
}

interface ProfileStatus {
  review_status: string;
  completion_percentage: number;
  photo_count: number;
  admin_comment?: string;
  requested_fields?: string[];
}

interface RecommendedProfile {
  id: number;
  profile_id?: string;
  first_name: string;
  last_name: string;
  age?: number;
  height_cm?: number;
  city?: string;
  state?: string;
  education?: string;
  occupation?: string;
  religion?: string;
  photos?: Array<{ id: number }>;
  reason?: string;
  can_view_contact?: boolean;
}

interface DailyMatchNotification {
  id: number;
  title: string;
  message: string;
  link?: string;
  is_read: boolean;
}

interface ProfileViewer {
  user_id: number;
  profile_id?: string;
  name: string;
  view_count: number;
  last_viewed_at?: string | null;
}

interface MatchPreferenceFieldConfig {
  field_key: string;
  options: string[];
}

interface ProfileFieldConfig {
  label: string;
  input_type: string;
  options: string[];
  placeholder?: string | null;
  helper_text?: string | null;
  is_enabled: boolean;
  is_required: boolean;
  depends_on?: string | null;
  grouped_options?: Record<string, string[]>;
}

// Height options as centimeters, displayed to the user as feet'inches" (e.g. 4'6" to 7'0").
const HEIGHT_OPTIONS_CM: string[] = Array.from({ length: 215 - 122 + 1 }, (_, i) => String(122 + i));
const formatCmAsFeetInches = (cmValue: string): string => {
  const cm = Number(cmValue);
  if (!cm) return '';
  const totalInches = Math.round(cm / 2.54);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return `${feet}'${inches}"`;
};

// Accepts plain cm ("175") or feet/inches text (4'1, 4'1", 4 ft 1 in) and returns cm.
const parseHeightToCm = (value: string): number | undefined => {
  const raw = String(value || '').trim();
  if (!raw) return undefined;
  const shorthandFeetInches = raw.match(/^([4-7])\.(\d{1,2})$/);
  if (shorthandFeetInches) {
    const feet = Number(shorthandFeetInches[1]);
    const inches = Number(shorthandFeetInches[2]);
    if (inches <= 11) return Math.round((feet * 12 + inches) * 2.54);
    return undefined;
  }
  if (/^\d+(\.\d+)?$/.test(raw)) {
    const cm = Number(raw);
    return Number.isFinite(cm) ? Math.round(cm) : undefined;
  }
  const match = raw.match(/(\d+)\s*(?:'|ft)?\s*(\d+)?/);
  if (!match) return undefined;
  const feet = Number(match[1]);
  const inches = Number(match[2] || 0);
  if (!Number.isFinite(feet)) return undefined;
  return Math.round((feet * 12 + inches) * 2.54);
};

const TIME_HOURS_OPTIONS = Array.from({ length: 24 }, (_, value) => String(value).padStart(2, '0'));
const TIME_MINUTES_OPTIONS = Array.from({ length: 60 }, (_, value) => String(value).padStart(2, '0'));
const TIME_12_HOUR_OPTIONS = Array.from({ length: 12 }, (_, value) => String(value + 1).padStart(2, '0'));

const parseTimeValue = (value: string): { hours: string; minutes: string } => {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) return { hours: '', minutes: '' };
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return { hours: '', minutes: '' };
  }
  return { hours: String(hours).padStart(2, '0'), minutes: String(minutes).padStart(2, '0') };
};

const buildTimeValue = (hours: string, minutes: string): string => {
  if (!hours || !minutes) return '';
  return `${hours}:${minutes}`;
};

const normalizeTimeValue = (value?: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!match) return '';
  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const period = match[3]?.toUpperCase();
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes > 59) return '';
  if (period) {
    if (hours < 1 || hours > 12) return '';
    hours = hours % 12 + (period === 'PM' ? 12 : 0);
  } else if (hours < 0 || hours > 23) {
    return '';
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

interface CompleteProfileFormState {
  [key: string]: string;
  first_name: string;
  last_name: string;
  gender: string;
  date_of_birth: string;
  sub_caste: string;
  ekdo: string;
  staying_with_family: string;
  current_country: string;
  height_cm: string;
  religion: string;
  caste: string;
  gotra: string;
  dosham_status: string;
  mother_tongue: string;
  place_of_birth: string;
  qualification_field: string;
  college_name: string;
  college_city: string;
  university_name: string;
  candidate_aadhar_card: string;
  father_aadhar_card: string;
  time_of_birth: string;
  marital_status: string;
  education: string;
  annual_income: string;
  occupation: string;
  current_company_name: string;
  company_type: string;
  work_location: string;
  work_time: string;
  work_experience: string;
  job_type: string;
  remark: string;
  hobbies: string;
  city: string;
  state: string;
  family_type: string;
  permanent_address: string;
  pin_code: string;
  about_me: string;
  father_name: string;
  mother_name: string;
  father_occupation: string;
  mother_occupation: string;
  father_alive: string;
  mother_alive: string;
  father_mobile: string;
  mother_mobile: string;
  has_siblings: string;
  siblings_count: string;
  brother_count: string;
  brother_marriage_status: string;
  sister_count: string;
  sister_marriage_status: string;
  grandfather_name: string;
  grandmother_name: string;
  native_place: string;
  permanent_country: string;
  permanent_property_type: string;
  permanent_property_details: string;
  current_address: string;
  current_country_address: string;
  current_state: string;
  current_city: string;
  current_pin_code: string;
  current_property_type: string;
  current_property_details: string;
  candidate_aadhaar_number: string;
  parent_aadhaar_number: string;
  candidate_aadhaar_back: string;
  parent_aadhaar_back: string;
}

const formatDateForInput = (value?: string): string => value ? value.slice(0, 10) : '';

const isImageDataUrl = (value?: string) => typeof value === 'string' && value.startsWith('data:image/');

const initialForm: CompleteProfileFormState = {
  first_name: '',
  last_name: '',
  gender: '',
  date_of_birth: '',
  sub_caste: '',
  ekdo: '',
  staying_with_family: '',
  current_country: 'India',
  height_cm: '',
  religion: '',
  caste: '',
  gotra: '',
  dosham_status: '',
  mother_tongue: '',
  place_of_birth: '',
  qualification_field: '',
  college_name: '',
  college_city: '',
  university_name: '',
  candidate_aadhar_card: '',
  father_aadhar_card: '',
  time_of_birth: '',
  marital_status: 'NEVER_MARRIED',
  education: '',
  annual_income: '',
  occupation: '',
  current_company_name: '',
  company_type: '',
  work_location: '',
  work_time: '',
  work_experience: '',
  job_type: '',
  remark: '',
  hobbies: '',
  city: '',
  state: '',
  family_type: '',
  permanent_address: '',
  pin_code: '',
  about_me: '',
  father_name: '',
  mother_name: '',
  father_occupation: '',
  mother_occupation: '',
  father_alive: 'YES',
  mother_alive: 'YES',
  father_mobile: '',
  mother_mobile: '',
  has_siblings: 'YES',
  siblings_count: '',
  brother_count: '',
  brother_marriage_status: '',
  sister_count: '',
  sister_marriage_status: '',
  grandfather_name: '',
  grandmother_name: '',
  native_place: '',
  permanent_country: 'India',
  permanent_property_type: '',
  permanent_property_details: '',
  current_address: '',
  current_country_address: 'India',
  current_state: '',
  current_city: '',
  current_pin_code: '',
  current_property_type: '',
  current_property_details: '',
  candidate_aadhaar_number: '',
  parent_aadhaar_number: '',
  candidate_aadhaar_back: '',
  parent_aadhaar_back: '',
};

const REMOVED_SENSITIVE_PROFILE_FIELDS = new Set([
  'candidate_aadhar_card',
  'father_aadhar_card',
  'candidate_aadhaar_number',
  'parent_aadhaar_number',
  'candidate_aadhaar_back',
  'parent_aadhaar_back',
]);

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const mediaOrigin = String(api.defaults.baseURL || '').replace(/\/?api\/?$/, '');
  const [mobileOpen, setMobileOpen] = useState(false);
  // Resolves admin-configured dropdown options for a profile field, honoring depends_on (e.g. caste depends on religion).
  const getFieldOptions = (config: Record<string, ProfileFieldConfig>, key: string, currentValues: CompleteProfileFormState): { isDropdown: boolean; options: string[] } => {
    const cfg = config[key];
    if (!cfg || (cfg.input_type !== 'select' && cfg.input_type !== 'grouped_select')) {
      return { isDropdown: false, options: [] };
    }
    const dependsOnKey = cfg.depends_on || '';
    const hasGroupedDependency = Boolean(dependsOnKey && Object.keys(cfg.grouped_options || {}).length > 0);
    if (hasGroupedDependency) {
      const dependValue = String((currentValues as Record<string, string>)[dependsOnKey] || '');
      const grouped = cfg.grouped_options || {};
      const matchingKey = Object.keys(grouped).find((key) => key.trim().toLowerCase() === dependValue.trim().toLowerCase());
      const options = matchingKey ? grouped[matchingKey] || [] : [];
      return { isDropdown: true, options };
    }
    return { isDropdown: cfg.input_type === 'select' || cfg.input_type === 'grouped_select' || cfg.options.length > 0, options: cfg.options };
  };
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [profileStatus, setProfileStatus] = useState<ProfileStatus | null>(null);
  const [recommendedProfiles, setRecommendedProfiles] = useState<RecommendedProfile[]>([]);
  const [dailyMatchNotifications, setDailyMatchNotifications] = useState<DailyMatchNotification[]>([]);
  const [unreadDailyMatchNotifications, setUnreadDailyMatchNotifications] = useState(0);
  const [profileViewers, setProfileViewers] = useState<ProfileViewer[]>([]);
  const [loading, setLoading] = useState(true);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [matchPreferenceDialogOpen, setMatchPreferenceDialogOpen] = useState(false);
  const [matchPreferenceLoading, setMatchPreferenceLoading] = useState(false);
  const [matchPreferenceSaving, setMatchPreferenceSaving] = useState(false);
  const [matchPreferences, setMatchPreferences] = useState({ min_age: 21, max_age: 30, marital_status: 'NO_PREFERENCE', location_scope: 'ANYWHERE', religion: 'NO_PREFERENCE', mother_tongue: 'NO_PREFERENCE', preferred_community: 'NO_PREFERENCE', preferred_caste: 'NO_PREFERENCE', preferred_education: 'NO_PREFERENCE', preferred_current_country: 'NO_PREFERENCE', fallback_mode: 'SAME_LANGUAGE_RELIGION' });
  const [matchPreferenceOptions, setMatchPreferenceOptions] = useState<Record<string, string[]>>({});
  const [profileFieldConfig, setProfileFieldConfig] = useState<Record<string, ProfileFieldConfig>>({});
  const [profileSubmitting, setProfileSubmitting] = useState(false);
  const [draftAutosaveReady, setDraftAutosaveReady] = useState(false);
  const [formData, setFormData] = useState<CompleteProfileFormState>(initialForm);
  const [currentAddressSameAsPermanent, setCurrentAddressSameAsPermanent] = useState(false);
  const [primaryPhoto, setPrimaryPhoto] = useState('');
  const [additionalPhotos, setAdditionalPhotos] = useState<string[]>([]);
  const [existingGalleryPhotos, setExistingGalleryPhotos] = useState<string[]>([]);
  const [imagePreview, setImagePreview] = useState<{ src: string; label: string } | null>(null);
  const [currentProfilePhotoUrl, setCurrentProfilePhotoUrl] = useState('');
  const profileNeedsReverification = profileStatus?.review_status === 'APPROVED' || (profileStatus?.completion_percentage || 0) >= 100;

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (['SUPER_ADMIN', 'CONTENT_MODERATOR', 'FINANCE_ADMIN', 'SUPPORT_ADMIN', 'ID_VERIFICATION_ADMIN'].includes(user.role || '')) {
      navigate('/admin');
      return;
    }
    void loadDashboard();
  }, [user?.id, navigate]);

  useEffect(() => {
    const registrationRequestedOpen = Boolean((location.state as { openCompleteProfile?: boolean } | null)?.openCompleteProfile);
    const shouldOpenFromState = registrationRequestedOpen && window.sessionStorage.getItem(OPEN_COMPLETE_PROFILE_ONCE_KEY) === '1';
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenFromQuery = searchParams.get('openCompleteProfile') === '1';
    const shouldOpenMatchPreferences = searchParams.get('openMatchPreferences') === '1';
    if (!loading && (shouldOpenFromState || shouldOpenFromQuery || shouldOpenMatchPreferences)) {
      window.sessionStorage.removeItem(OPEN_COMPLETE_PROFILE_ONCE_KEY);
      navigate(location.pathname, { replace: true, state: null });
      if (shouldOpenMatchPreferences) void openMatchPreferences();
      else void openCompleteProfile();
    } else if (!loading && registrationRequestedOpen) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [loading, location.pathname, location.search, location.state, navigate]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [summaryResponse, statusResponse, recommendedResponse, notificationResponse, viewersResponse] = await Promise.all([
        api.get('/users/me/dashboard-summary'),
        api.get('/users/me/profile-status'),
        api.get('/daily-matches/today'),
        api.get('/daily-matches/notifications'),
        api.get('/users/me/profile-viewers'),
      ]);
      setSummary(summaryResponse.data);
      setProfileStatus(statusResponse.data);
      setRecommendedProfiles(recommendedResponse.data.items || []);
      setDailyMatchNotifications(notificationResponse.data.items || []);
      setUnreadDailyMatchNotifications(notificationResponse.data.unread_count || 0);
      setProfileViewers(viewersResponse.data.items || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const markDailyMatchNotificationsRead = async () => {
    if (!unreadDailyMatchNotifications) return;
    try {
      await api.post('/daily-matches/notifications/read');
      setUnreadDailyMatchNotifications(0);
      setDailyMatchNotifications((previous) => previous.map((notification) => ({ ...notification, is_read: true })));
    } catch {
      toast.error('Failed to mark notifications as read');
    }
  };

  const firstUnreadNotification = dailyMatchNotifications.find((notification) => !notification.is_read);

  const handleNotificationAction = async (notification: DailyMatchNotification) => {
    await markDailyMatchNotificationsRead();
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const openMatchPreferences = async () => {
    try {
      setMatchPreferenceDialogOpen(true);
      setMatchPreferenceLoading(true);
      const [preferenceResponse, fieldsResponse, optionsResponse, profileFieldsResponse] = await Promise.all([
        api.get('/daily-matches/preferences'),
        api.get<{ items: MatchPreferenceFieldConfig[] }>('/profiles/filter-config'),
        api.get<Record<string, string[]>>('/profiles/filter-options'),
        api.get<{ items: Array<{ field_key: string; options: string[] }> }>('/users/public/profile-fields'),
      ]);
      if (preferenceResponse.data.preferences) {
        setMatchPreferences(preferenceResponse.data.preferences);
      }
      const configuredOptions = Object.fromEntries(
        (fieldsResponse.data.items || []).map((item) => [
          item.field_key,
          (item.options || []).length > 0 ? item.options : (optionsResponse.data[item.field_key] || []),
        ])
      ) as Record<string, string[]>;
      const educationFieldOptions = profileFieldsResponse.data.items.find((item) => item.field_key === 'education')?.options || [];
      const preferenceOptionKeys = ['religion', 'mother_tongue', 'sub_caste', 'caste', 'education', 'current_country'];
      for (const key of preferenceOptionKeys) {
        configuredOptions[key] = Array.from(new Set([
          ...(configuredOptions[key] || []),
          ...(optionsResponse.data[key] || []),
          ...(key === 'education' ? educationFieldOptions : []),
        ]));
      }
      setMatchPreferenceOptions(configuredOptions);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load match preferences');
      setMatchPreferenceDialogOpen(false);
    } finally {
      setMatchPreferenceLoading(false);
    }
  };

  const saveMatchPreferences = async () => {
    if (matchPreferences.min_age < 18 || matchPreferences.max_age > 80 || matchPreferences.min_age > matchPreferences.max_age) {
      toast.error('Preferred ages must be between 18 and 80, and the minimum cannot exceed the maximum');
      return;
    }
    try {
      setMatchPreferenceSaving(true);
      const response = await api.put('/daily-matches/preferences', matchPreferences);
      setMatchPreferences(response.data.preferences || matchPreferences);
      setMatchPreferenceDialogOpen(false);
      toast.success('Match preferences saved successfully.');
      void loadDashboard();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save match preferences');
    } finally {
      setMatchPreferenceSaving(false);
    }
  };

  const openCompleteProfile = async () => {
    try {
      setDraftAutosaveReady(false);
      const [draftResult, profileFieldsResult] = await Promise.allSettled([
        api.get('/users/me/profile-draft'),
        api.get<{ items: Array<{ field_key: string; label: string; input_type: string; options: string[]; placeholder?: string | null; helper_text?: string | null; is_enabled: boolean; is_required: boolean; depends_on?: string | null; grouped_options?: Record<string, string[]> }> }>(`/users/public/profile-fields?config_version=${Date.now()}`),
      ]);

      if (draftResult.status !== 'fulfilled') {
        throw new Error('Failed to load profile draft');
      }
      if (profileFieldsResult.status !== 'fulfilled') {
        toast.error('Failed to load latest admin field settings. Please try again.');
        return;
      }

      const draft = draftResult.value.data || {};
      const userProfile = draft.user_profile || {};
      const extended = draft.extended_profile || {};
      const savedHeight = userProfile.height_cm ?? extended.height_cm ?? '';
      setFormData({
        ...initialForm,
        ...Object.fromEntries(Object.entries(extended).map(([key, value]) => [key, value == null ? '' : String(value)])),
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        gender: user?.gender || '',
        date_of_birth: formatDateForInput(userProfile.date_of_birth || user?.date_of_birth),
          sub_caste: userProfile.sub_caste || '',
        ekdo: extended.ekdo || '',
        staying_with_family: extended.staying_with_family || '',
        current_country: extended.current_country || 'India',
        height_cm: String(parseHeightToCm(String(savedHeight)) ?? ''),
        religion: userProfile.religion || '',
        caste: userProfile.caste || '',
        gotra: extended.gotra || '',
        dosham_status: extended.dosham_status || userProfile.dosham_status || '',
        mother_tongue: extended.mother_tongue || '',
        place_of_birth: extended.place_of_birth || '',
        qualification_field: extended.qualification_field || '',
        college_name: extended.college_name || '',
        college_city: extended.college_city || '',
        university_name: extended.university_name || '',
        candidate_aadhar_card: extended.candidate_aadhar_card || '',
        father_aadhar_card: extended.father_aadhar_card || '',
        time_of_birth: extended.time_of_birth || '',
        marital_status: userProfile.marital_status || 'NEVER_MARRIED',
        education: userProfile.education || '',
        annual_income: userProfile.annual_income || '',
        occupation: userProfile.occupation || '',
        current_company_name: extended.current_company_name || '',
        company_type: extended.company_type || '',
        work_location: extended.work_location || '',
        work_time: extended.work_time || '',
        work_experience: extended.work_experience || '',
        job_type: extended.job_type || '',
        remark: extended.remark || '',
        hobbies: extended.hobbies || userProfile.hobbies || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        family_type: extended.family_type || '',
        permanent_address: extended.permanent_address || '',
        pin_code: extended.pin_code || '',
        about_me: userProfile.about_me || '',
        father_name: extended.father_name || '',
        mother_name: extended.mother_name || '',
        father_occupation: extended.father_occupation || '',
        mother_occupation: extended.mother_occupation || '',
        father_alive: extended.father_alive || 'YES',
        mother_alive: extended.mother_alive || 'YES',
        father_mobile: extended.father_mobile || '',
        mother_mobile: extended.mother_mobile || '',
        has_siblings: extended.has_siblings || (Number(extended.siblings_count) === 0 ? 'NO' : 'YES'),
        siblings_count: extended.siblings_count !== undefined && extended.siblings_count !== null ? String(extended.siblings_count) : '',
        brother_count: extended.brother_count !== undefined && extended.brother_count !== null ? String(extended.brother_count) : '',
        brother_marriage_status: extended.brother_marriage_status || '',
        sister_count: extended.sister_count !== undefined && extended.sister_count !== null ? String(extended.sister_count) : '',
        sister_marriage_status: extended.sister_marriage_status || '',
        grandfather_name: extended.grandfather_name || '',
        grandmother_name: extended.grandmother_name || '',
        native_place: extended.native_place || '',
        permanent_country: extended.permanent_country || 'India',
        permanent_property_type: extended.permanent_property_type || '',
        permanent_property_details: extended.permanent_property_details || '',
        current_address: extended.current_address || '',
        current_country_address: extended.current_country_address || 'India',
        current_state: extended.current_state || '',
        current_city: extended.current_city || '',
        current_pin_code: extended.current_pin_code || '',
        current_property_type: extended.current_property_type || '',
        current_property_details: extended.current_property_details || '',
        candidate_aadhaar_number: extended.candidate_aadhaar_number || '',
        parent_aadhaar_number: extended.parent_aadhaar_number || '',
        candidate_aadhaar_back: extended.candidate_aadhaar_back || '',
        parent_aadhaar_back: extended.parent_aadhaar_back || '',
      });
      setCurrentAddressSameAsPermanent(false);

      const profileItems = profileFieldsResult.value.data.items || [];
      setProfileFieldConfig(Object.fromEntries(
        profileItems.map((item) => [item.field_key, {
          label: item.label,
          input_type: item.input_type,
          options: item.options || [],
          placeholder: item.placeholder,
          helper_text: item.helper_text,
          is_enabled: item.is_enabled,
          is_required: item.is_required,
          depends_on: item.depends_on,
          grouped_options: item.grouped_options || {},
        }])
      ));
      setCurrentProfilePhotoUrl(draft.profile_photo_url || '');
      setExistingGalleryPhotos((draft.gallery_photo_urls || []).map((photoUrl: string) => `${mediaOrigin}${photoUrl}`));
      setPrimaryPhoto('');
      setAdditionalPhotos([]);
      setCompleteDialogOpen(true);
      setDraftAutosaveReady(true);
    } catch {
      setDraftAutosaveReady(false);
      setFormData(initialForm);
      setCurrentProfilePhotoUrl('');
      setExistingGalleryPhotos([]);
      setPrimaryPhoto('');
      setAdditionalPhotos([]);
      setCompleteDialogOpen(true);
    }
  };

  const readPhoto = (event: React.ChangeEvent<HTMLInputElement>, onLoaded: (dataUrl: string) => void) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onLoaded(String(reader.result));
      event.target.value = '';
    };
    reader.readAsDataURL(file);
  };

  const handlePrimaryPhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    readPhoto(event, setPrimaryPhoto);
  };

  const handleAdditionalPhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (existingGalleryPhotos.length + additionalPhotos.length >= 3) {
      toast.error('You can add up to 3 additional photos');
      event.target.value = '';
      return;
    }
    readPhoto(event, (dataUrl) => setAdditionalPhotos((previous) => [...previous, dataUrl]));
  };

  const handleDocumentUpload = (fieldName: 'candidate_aadhar_card' | 'father_aadhar_card' | 'candidate_aadhaar_back' | 'parent_aadhaar_back') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload Aadhaar card as an image');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, [fieldName]: String(reader.result) }));
      event.target.value = '';
    };
    reader.readAsDataURL(file);
  };

  const optionalNumber = (value: string) => {
    if (!String(value || '').trim()) return undefined;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : undefined;
  };

  const buildProfilePayload = (includePhotos: boolean) => ({
    first_name: formData.first_name || undefined,
    last_name: formData.last_name || undefined,
    gender: formData.gender || undefined,
    date_of_birth: formData.date_of_birth ? `${formData.date_of_birth}T00:00:00` : undefined,
    height_cm: parseHeightToCm(formData.height_cm),
    religion: formData.religion || undefined,
    sub_caste: formData.sub_caste || null,
    caste: formData.caste || null,
    dosham_status: formData.dosham_status || null,
    marital_status: formData.marital_status || undefined,
    education: formData.education || undefined,
    occupation: formData.occupation || undefined,
    annual_income: formData.annual_income || null,
    city: formData.city || undefined,
    state: formData.state || undefined,
    about_me: formData.about_me || undefined,
    hobbies: null,
    languages: null,
    ...(includePhotos ? { photo_data_urls: primaryPhoto ? [primaryPhoto, ...additionalPhotos] : additionalPhotos } : {}),
    extended_profile: {
      ...Object.fromEntries(Object.entries(formData).filter(([key]) => !['first_name', 'last_name', 'gender', 'date_of_birth', 'height_cm', 'religion', 'caste', 'sub_caste', 'marital_status', 'education', 'occupation', 'annual_income', 'city', 'state', 'about_me'].includes(key))),
      ekdo: formData.ekdo,
      staying_with_family: formData.staying_with_family,
      current_country: formData.current_country,
      time_of_birth: normalizeTimeValue(formData.time_of_birth),
      gotra: formData.gotra,
      mother_tongue: formData.mother_tongue,
      qualification_field: formData.qualification_field,
      college_name: formData.college_name,
      college_city: formData.college_city,
      university_name: formData.university_name,
      current_company_name: formData.current_company_name,
      company_type: formData.company_type,
      work_location: formData.work_location,
      work_time: formData.work_time,
      work_experience: formData.work_experience,
      job_type: formData.job_type,
      remark: formData.remark,
      hobbies: formData.hobbies,
      family_type: formData.family_type,
      permanent_address: formData.permanent_address,
      pin_code: formData.pin_code,
      father_alive: formData.father_alive,
      mother_alive: formData.mother_alive,
      father_mobile: formData.father_mobile || null,
      mother_mobile: formData.mother_mobile || null,
      has_siblings: formData.has_siblings,
      siblings_count: formData.has_siblings === 'YES' ? optionalNumber(formData.siblings_count) : undefined,
      brother_count: optionalNumber(formData.brother_count),
      brother_marriage_status: formData.brother_marriage_status,
      sister_count: optionalNumber(formData.sister_count),
      sister_marriage_status: formData.sister_marriage_status,
      grandfather_name: formData.grandfather_name,
      grandmother_name: formData.grandmother_name,
      native_place: formData.native_place,
      permanent_country: formData.permanent_country,
      permanent_property_type: formData.permanent_property_type,
      permanent_property_details: formData.permanent_property_details,
      current_address: formData.current_address,
      current_country_address: formData.current_country_address,
      current_state: formData.current_state,
      current_city: formData.current_city,
      current_pin_code: formData.current_pin_code,
      current_property_type: formData.current_property_type,
      current_property_details: formData.current_property_details,
    },
  });

  useEffect(() => {
    if (!completeDialogOpen || !draftAutosaveReady || !Object.keys(profileFieldConfig).length) return;
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await api.put('/users/me/profile-draft', buildProfilePayload(false));
        setProfileStatus(response.data);
      } catch {
        // Draft autosave is intentionally silent; final submit shows validation errors.
      }
    }, 1500);
    return () => window.clearTimeout(timeoutId);
  }, [completeDialogOpen, draftAutosaveReady, formData, profileFieldConfig]);

  const submitCompleteProfile = async () => {
    const hiddenProfileFields = ['has_siblings', 'siblings_count', 'siblings_info', 'residency_status'];
    const missingRequiredField = Object.entries(profileFieldConfig).find(([fieldKey, config]) => {
      if (!config.is_enabled || !config.is_required || hiddenProfileFields.includes(fieldKey)) return false;
      if (REMOVED_SENSITIVE_PROFILE_FIELDS.has(fieldKey)) return false;
      const value = fieldKey === 'time_of_birth'
        ? normalizeTimeValue(formData.time_of_birth)
        : String(formData[fieldKey] || '').trim();
      return !value;
    });
    if (missingRequiredField) {
      toast.error(`${missingRequiredField[1].label} is required`);
      return;
    }

    if (additionalPhotos.length > 0 && !primaryPhoto) {
      toast.error('Upload a Profile Photo before adding gallery photos');
      return;
    }

    try {
      setProfileSubmitting(true);
      await api.post('/users/me/complete-profile', buildProfilePayload(true));
      toast.success(profileNeedsReverification ? 'Profile submitted for reverification' : 'Profile submitted for review');
      setCompleteDialogOpen(false);
      void loadDashboard();
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      const validationMessage = Array.isArray(detail)
        ? detail.map((item) => `${(item.loc || []).join('.')}: ${item.msg}`).join('; ')
        : detail;
      toast.error(validationMessage || 'Failed to submit profile');
    } finally {
      setProfileSubmitting(false);
    }
  };

  const saveProfileAndContinueLater = async () => {
    try {
      setProfileSubmitting(true);
      const response = await api.put('/users/me/profile-draft', buildProfilePayload(false));
      setProfileStatus(response.data);
      setCompleteDialogOpen(false);
      toast.success('Profile saved. You can continue later.');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save profile draft');
    } finally {
      setProfileSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getPhoto = (profile: RecommendedProfile) => {
    if (!profile.photos?.length) return '';
    return `${String(api.defaults.baseURL || '').replace(/\/?api\/?$/, '')}/api/profiles/photos/${profile.photos[0].id}`;
  };

  const sidebarItems = useMemo(
    () => [
      { label: 'Dashboard', icon: <Home fontSize="small" />, action: () => navigate('/my-profile'), active: true },
      { label: profileNeedsReverification ? 'Update Profile' : 'Complete Profile', icon: <PersonOutline fontSize="small" />, action: openCompleteProfile },
      { label: 'Match Preferences', icon: <Settings fontSize="small" />, action: openMatchPreferences },
      { label: 'Search Profiles', icon: <Home fontSize="small" />, action: () => navigate('/') },
      { label: 'Unlocked Profiles', icon: <FavoriteBorder fontSize="small" />, action: () => navigate('/my-connections') },
      { label: 'Recharge Account', icon: <Wallet fontSize="small" />, action: () => navigate('/wallet') },
      { label: 'Activation Status', icon: <VerifiedUser fontSize="small" />, action: () => navigate('/activation-status') },
      { label: 'Change Password', icon: <Settings fontSize="small" />, action: () => navigate('/change-password') },
      { label: 'Logout', icon: <Logout fontSize="small" />, action: handleLogout },
    ],
    [navigate]
  );

  const sideBarContent = (
    <Box className="maandvo-dashboard-sidebar" role="presentation">
      <Box className="maandvo-dashboard-brand">
        <Box component="img" src="/maandvo-logo.png" alt="Brand" />
      </Box>
      <Box sx={{ px: 2, py: 1.4, backgroundColor: '#6d0010', borderBottom: '1px solid rgba(255,216,154,0.28)' }}>
        <Typography sx={{ color: '#ffd89a', fontSize: '0.78rem', fontWeight: 800 }}>Membership No.</Typography>
        <Typography sx={{ color: '#fff6ea', fontSize: '0.98rem', fontWeight: 800 }}>{user?.profile_id || 'Pending'}</Typography>
      </Box>
      <Box className="maandvo-dashboard-nav">
        {sidebarItems.map((item) => (
          <Box key={item.label} className={`maandvo-dashboard-nav-item ${item.active ? 'active' : ''}`} onClick={item.action}>
            {item.icon}
            <span>{item.label}</span>
          </Box>
        ))}
      </Box>
      <Box className="maandvo-dashboard-premium">
        <Typography sx={{ color: '#ffd89a', fontWeight: 800, mb: 0.7 }}>Membership & Account</Typography>
        <Typography sx={{ color: '#fff6ea', fontSize: '0.9rem', mb: 1.2 }}>
          Account balance: {summary?.wallet_balance ?? 0} Points
        </Typography>
        <Button fullWidth variant="contained" sx={{ backgroundColor: '#ffb935', color: '#5d000d', fontWeight: 800 }} onClick={() => navigate('/wallet')}>
          Recharge Account
        </Button>
      </Box>
    </Box>
  );

  const statCards = [
    { label: 'Account Balance', value: `${summary?.wallet_balance ?? 0} Points`, icon: <Wallet sx={{ color: MAROON }} /> },
    { label: 'Unlocked Profiles', value: summary?.connections_count ?? 0, icon: <FavoriteBorder sx={{ color: MAROON }} /> },
    { label: 'Profile Views', value: summary?.profile_views ?? 0, icon: <Visibility sx={{ color: MAROON }} /> },
  ];

  const renderConfiguredField = (fieldKey: string, forceDisabled = false) => {
    const config = profileFieldConfig[fieldKey];
    if (!config?.is_enabled || REMOVED_SENSITIVE_PROFILE_FIELDS.has(fieldKey)) return null;
    const value = formData[fieldKey] || '';
    const options = getFieldOptions(profileFieldConfig, fieldKey, formData);
    const dependsOnKey = config.depends_on || '';
    const hasGroupedDependency = Boolean(dependsOnKey && Object.keys(config.grouped_options || {}).length > 0);
    const dependsOnValue = hasGroupedDependency ? Boolean((formData as Record<string, string>)[dependsOnKey]) : true;
    const updateValue = (nextValue: string) => setFormData((previous) => {
      const nextForm = { ...previous, [fieldKey]: nextValue };
      Object.entries(profileFieldConfig).forEach(([childKey, childConfig]) => {
        if (childConfig.depends_on === fieldKey) nextForm[childKey] = '';
      });
      return nextForm;
    });
    const normalizedInputType = String(config.input_type || '').toLowerCase();
    const effectiveInputType = fieldKey === 'time_of_birth'
      ? 'time_12_hour'
      : ['permanent_address', 'current_address'].includes(fieldKey)
        ? 'textarea'
      : fieldKey === 'height_cm'
        ? 'height'
        : normalizedInputType;
    const userHelperText = ['caste', 'sub_caste'].includes(fieldKey) ? undefined : (config.helper_text || undefined);
    const commonProps = {
      label: config.label,
      value,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateValue(event.target.value),
      required: config.is_required,
      placeholder: config.placeholder || undefined,
      helperText: userHelperText,
      disabled: forceDisabled || !dependsOnValue,
      size: 'small' as const,
      sx: forceDisabled ? {
        '& .MuiInputBase-root.Mui-disabled': {
          backgroundColor: '#f7f1e8',
          '& input, & .MuiSelect-select': {
            WebkitTextFillColor: '#332a26',
            color: '#332a26',
            opacity: 1,
          },
        },
      } : undefined,
    };

    if (forceDisabled) {
      return <TextField key={fieldKey} label={config.label} value={value} required={config.is_required} size="small" InputProps={{ readOnly: true }} helperText="Cannot be changed after registration" sx={commonProps.sx} />;
    }

    if (effectiveInputType === 'height') {
      const normalizedHeight = parseHeightToCm(value);
      const normalizedHeightValue = normalizedHeight === undefined ? value : String(normalizedHeight);
      const heightOptions = normalizedHeightValue && !HEIGHT_OPTIONS_CM.includes(normalizedHeightValue)
        ? [normalizedHeightValue, ...HEIGHT_OPTIONS_CM]
        : HEIGHT_OPTIONS_CM;
      return <Autocomplete key={fieldKey} options={heightOptions} getOptionLabel={formatCmAsFeetInches} isOptionEqualToValue={(option, selected) => option === selected} value={normalizedHeightValue || null} disabled={forceDisabled || !dependsOnValue} onChange={(_event, nextValue) => updateValue(nextValue || '')} renderInput={(params) => <TextField {...params} label={config.label} required={config.is_required} helperText={userHelperText} size="small" />} />;
    }
    if (effectiveInputType === 'time_12_hour') {
      const normalizedTime = normalizeTimeValue(value);
      const [storedHours = '', storedMinutes = ''] = normalizedTime.split(':');
      const hourNumber = Number(storedHours);
      const selectedPeriod = normalizedTime ? (hourNumber >= 12 ? 'PM' : 'AM') : '';
      const selectedHour = normalizedTime ? String(hourNumber % 12 || 12).padStart(2, '0') : '';
      const updateTime = (hour: string, minute: string, period: string) => {
        const resolvedHour = hour || selectedHour || '12';
        const resolvedMinute = minute || storedMinutes || '00';
        const resolvedPeriod = period || selectedPeriod || 'AM';
        const hours24 = (Number(resolvedHour) % 12) + (resolvedPeriod === 'PM' ? 12 : 0);
        updateValue(`${String(hours24).padStart(2, '0')}:${resolvedMinute}`);
      };
      return <React.Fragment key={fieldKey}>
        <TextField select label={`${config.label} Hour`} value={selectedHour} required={config.is_required} disabled={forceDisabled || !dependsOnValue} size="small" onChange={(event) => updateTime(event.target.value, storedMinutes, selectedPeriod)}>
          {TIME_12_HOUR_OPTIONS.map((hourOption) => <MenuItem key={hourOption} value={hourOption}>{hourOption}</MenuItem>)}
        </TextField>
        <TextField select label="Minute" value={storedMinutes} required={config.is_required} disabled={forceDisabled || !dependsOnValue} size="small" onChange={(event) => updateTime(selectedHour, event.target.value, selectedPeriod)}>
          {TIME_MINUTES_OPTIONS.map((minuteOption) => <MenuItem key={minuteOption} value={minuteOption}>{minuteOption}</MenuItem>)}
        </TextField>
        <TextField select label="AM / PM" value={selectedPeriod} required={config.is_required} disabled={forceDisabled || !dependsOnValue} size="small" onChange={(event) => updateTime(selectedHour, storedMinutes, event.target.value)}>
          <MenuItem value="AM">AM</MenuItem>
          <MenuItem value="PM">PM</MenuItem>
        </TextField>
      </React.Fragment>;
    }
    if (effectiveInputType === 'time_dropdown' || effectiveInputType === 'time_hh_mm') {
      const { hours, minutes } = parseTimeValue(value);
      return <React.Fragment key={fieldKey}>
        <TextField
          select
          label={`${config.label} (HH)`}
          value={hours}
          required={config.is_required}
          disabled={forceDisabled || !dependsOnValue}
          size="small"
          onChange={(event) => updateValue(buildTimeValue(event.target.value, minutes))}
        >
          {TIME_HOURS_OPTIONS.map((hourOption) => <MenuItem key={hourOption} value={hourOption}>{hourOption}</MenuItem>)}
        </TextField>
        <TextField
          select
          label={`${config.label} (MM)`}
          value={minutes}
          required={config.is_required}
          disabled={forceDisabled || !dependsOnValue}
          size="small"
          onChange={(event) => updateValue(buildTimeValue(hours, event.target.value))}
        >
          {TIME_MINUTES_OPTIONS.map((minuteOption) => <MenuItem key={minuteOption} value={minuteOption}>{minuteOption}</MenuItem>)}
        </TextField>
      </React.Fragment>;
    }
    if (effectiveInputType === 'select' || effectiveInputType === 'grouped_select') {
      return <TextField key={fieldKey} {...commonProps} select>{(options.isDropdown ? options.options : config.options || []).map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}</TextField>;
    }
    if (effectiveInputType === 'date') return <TextField key={fieldKey} {...commonProps} type="date" InputLabelProps={{ shrink: true }} />;
    if (effectiveInputType === 'phone') return <TextField key={fieldKey} {...commonProps} type="tel" />;
    return <TextField key={fieldKey} {...commonProps} multiline={effectiveInputType === 'textarea'} minRows={effectiveInputType === 'textarea' ? (['permanent_address', 'current_address'].includes(fieldKey) ? 4 : 3) : undefined} sx={effectiveInputType === 'textarea' ? { ...commonProps.sx, gridColumn: { md: '1 / -1' } } : commonProps.sx} />;
  };

  const renderConfiguredSection = (title: string, fieldKeys: string[], forceDisabled = false) => {
    const fields = fieldKeys.filter((fieldKey) => profileFieldConfig[fieldKey]?.is_enabled).map((fieldKey) => renderConfiguredField(fieldKey, forceDisabled)).filter(Boolean);
    if (!fields.length) return null;
    return <React.Fragment key={title}><Typography sx={{ gridColumn: { md: '1 / -1' }, color: MAROON, fontWeight: 800, borderBottom: '1px solid #ead7be', pb: 0.7, mt: 1 }}>{title}</Typography>{fields}</React.Fragment>;
  };

  const renderConfiguredForm = () => {
    const identityConfig = (key: string, fallback: string) => profileFieldConfig[key]?.label || fallback;
    const identityRequired = (key: string, fallback: boolean) => profileFieldConfig[key]?.is_required ?? fallback;
    const setIdentity = (key: string, value: string) => setFormData((previous) => ({ ...previous, [key]: value }));
    const sectionFields = {
      personal: ['religion', 'caste', 'sub_caste', 'gotra', 'dosham_status', 'ekdo', 'mother_tongue', 'time_of_birth', 'place_of_birth', 'height_cm', 'marital_status', 'staying_with_family', 'current_country'],
      education: ['education', 'qualification_field', 'college_name', 'college_city', 'university_name'],
      professional: ['occupation', 'current_company_name', 'company_type', 'work_location', 'work_time', 'work_experience', 'job_type', 'annual_income', 'remark', 'about_me'],
      hobby: ['hobbies'],
      family: ['father_alive', 'father_name', 'father_occupation', 'father_mobile', 'mother_alive', 'mother_name', 'mother_occupation', 'mother_mobile', 'brother_count', 'brother_marriage_status', 'sister_count', 'sister_marriage_status', 'family_type', 'grandfather_name', 'grandmother_name', 'native_place'],
      permanentAddress: ['permanent_address', 'permanent_country', 'state', 'city', 'pin_code', 'permanent_property_type', 'permanent_property_details'],
      currentAddress: ['current_address', 'current_country_address', 'current_state', 'current_city', 'current_pin_code', 'current_property_type', 'current_property_details'],
      additional: ['has_siblings', 'siblings_count'],
    };
    const namedSectionFields = Object.values(sectionFields).reduce<string[]>((keys, section) => keys.concat(section), []);
    const excludedAdditionalFields = ['gender', 'date_of_birth', 'candidate_aadhar_card', 'father_aadhar_card', 'candidate_aadhaar_back', 'parent_aadhaar_back', 'candidate_aadhaar_number', 'parent_aadhaar_number'];
    const removedAdditionalFields = ['has_siblings', 'siblings_count', 'siblings_info', 'residency_status'];
    const additionalFields = Object.keys(profileFieldConfig).filter((fieldKey) => !namedSectionFields.includes(fieldKey) && !excludedAdditionalFields.includes(fieldKey) && !removedAdditionalFields.includes(fieldKey));
    return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
      <Typography sx={{ gridColumn: { md: '1 / -1' }, color: MAROON, fontWeight: 800, borderBottom: '1px solid #ead7be', pb: 0.7 }}>Personal Details</Typography>
      <TextField label={identityConfig('first_name', 'Candidate Name')} value={formData.first_name} required={identityRequired('first_name', true)} size="small" InputProps={{ readOnly: true }} helperText="Name cannot be changed after registration" />
      <TextField label={identityConfig('last_name', 'Candidate Last Name')} value={formData.last_name} required={identityRequired('last_name', true)} size="small" InputProps={{ readOnly: true }} helperText="Name cannot be changed after registration" />
      <TextField label="Email ID" value={user?.email || ''} size="small" InputProps={{ readOnly: true }} helperText="Email cannot be changed here" />
      {profileFieldConfig.gender?.is_enabled && renderConfiguredField('gender', true)}
      <TextField label="Mobile Number" value={user?.phone || ''} size="small" InputProps={{ readOnly: true }} helperText="Mobile number cannot be changed here" />
      {profileFieldConfig.date_of_birth?.is_enabled && renderConfiguredField('date_of_birth')}
      {sectionFields.personal.filter((fieldKey) => profileFieldConfig[fieldKey]?.is_enabled).map((fieldKey) => renderConfiguredField(fieldKey)).filter(Boolean)}
      {renderConfiguredSection('Highest Education Details', sectionFields.education)}
      {renderConfiguredSection('Professional Details', sectionFields.professional)}
      {renderConfiguredSection('Hobby Details', sectionFields.hobby)}
      {renderConfiguredSection('Family Details', sectionFields.family)}
      <Typography sx={{ gridColumn: { md: '1 / -1' }, color: MAROON, fontWeight: 800, borderBottom: '1px solid #ead7be', pb: 0.7, mt: 1 }}>Address Details</Typography>
      {renderConfiguredSection('Permanent Address', sectionFields.permanentAddress)}
      <FormControlLabel
        sx={{ gridColumn: { md: '1 / -1' }, ml: 0 }}
        control={<Checkbox checked={currentAddressSameAsPermanent} onChange={(event) => {
          const checked = event.target.checked;
          setCurrentAddressSameAsPermanent(checked);
          if (checked) {
            setFormData((previous) => ({
              ...previous,
              current_address: previous.permanent_address,
              current_country_address: previous.permanent_country,
              current_state: previous.state,
              current_city: previous.city,
              current_pin_code: previous.pin_code,
              current_property_type: previous.permanent_property_type,
              current_property_details: previous.permanent_property_details,
            }));
          }
        }} />}
        label="Current address is same as permanent address"
      />
      {renderConfiguredSection('Candidate Current Address', sectionFields.currentAddress, currentAddressSameAsPermanent)}
      {renderConfiguredSection('Additional Details', additionalFields)}
      <Box sx={{ gridColumn: { md: '1 / -1' } }}>
        <Typography sx={{ color: MAROON, fontWeight: 800, mb: 0.5 }}>Profile Photos</Typography>
        <Typography sx={{ color: '#7f7167', fontSize: '0.85rem', mb: 1.2 }}>The profile photo is used as your main photo across the platform. You may add up to 3 additional gallery photos.</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
          <Box>
            <Button component="label" variant="contained" fullWidth sx={{ backgroundColor: MAROON, '&:hover': { backgroundColor: '#5f000e' } }}>
              {primaryPhoto || currentProfilePhotoUrl ? 'Replace Profile Photo' : 'Upload Profile Photo'}
              <input hidden type="file" accept="image/*" onChange={handlePrimaryPhotoUpload} />
            </Button>
            {(primaryPhoto || currentProfilePhotoUrl) && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box component="img" src={primaryPhoto || `${mediaOrigin}${currentProfilePhotoUrl}`} alt="Primary profile preview" onClick={() => setImagePreview({ src: primaryPhoto || `${mediaOrigin}${currentProfilePhotoUrl}`, label: 'Profile Photo' })} sx={{ width: 112, height: 112, objectFit: 'cover', borderRadius: 1, border: '2px solid #ead1b0', cursor: 'zoom-in' }} />
                <Stack spacing={1}>
                  <Button size="small" variant="outlined" onClick={() => setImagePreview({ src: primaryPhoto || `${mediaOrigin}${currentProfilePhotoUrl}`, label: 'Profile Photo' })} sx={{ borderColor: MAROON, color: MAROON }}>View</Button>
                  {primaryPhoto && <Button size="small" color="error" variant="outlined" onClick={() => setPrimaryPhoto('')}>Delete</Button>}
                </Stack>
              </Box>
            )}
          </Box>
          <Box>
              <Button component="label" variant="outlined" fullWidth disabled={existingGalleryPhotos.length + additionalPhotos.length >= 3} sx={{ borderColor: MAROON, color: MAROON }}>
                Add Gallery Photo ({existingGalleryPhotos.length + additionalPhotos.length}/3)
              <input hidden type="file" accept="image/*" onChange={handleAdditionalPhotoUpload} />
            </Button>
            {(existingGalleryPhotos.length > 0 || additionalPhotos.length > 0) && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1 }}>
              {existingGalleryPhotos.map((photo, index) => <Box key={`existing-${photo}`} sx={{ position: 'relative', border: '1px solid #e6d4be', borderRadius: 1, p: 0.5 }}><Box component="img" src={photo} alt={`Gallery photo ${index + 1}`} onClick={() => setImagePreview({ src: photo, label: `Gallery Photo ${index + 1}` })} sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in', display: 'block' }} /><Button size="small" onClick={() => setImagePreview({ src: photo, label: `Gallery Photo ${index + 1}` })} sx={{ minWidth: 44, color: MAROON, fontSize: '0.7rem' }}>View</Button></Box>)}
              {additionalPhotos.map((photo, index) => <Box key={`${photo}-${index}`} sx={{ position: 'relative', border: '1px solid #e6d4be', borderRadius: 1, p: 0.5 }}><Box component="img" src={photo} alt={`New gallery preview ${existingGalleryPhotos.length + index + 1}`} onClick={() => setImagePreview({ src: photo, label: `New Gallery Photo ${index + 1}` })} sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in', display: 'block' }} /><Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}><Button size="small" onClick={() => setImagePreview({ src: photo, label: `New Gallery Photo ${index + 1}` })} sx={{ minWidth: 44, color: MAROON, fontSize: '0.7rem' }}>View</Button><Button size="small" color="error" onClick={() => setAdditionalPhotos((previous) => previous.filter((_, photoIndex) => photoIndex !== index))} sx={{ minWidth: 52, fontSize: '0.7rem' }}>Delete</Button></Stack></Box>)}
            </Box>}
          </Box>
        </Box>
      </Box>
    </Box>;
  };

  return (
    <Box className="maandvo-dashboard">
      <Box className="maandvo-dashboard-mobile-top">
        <IconButton aria-label="Open dashboard navigation" onClick={() => setMobileOpen(true)} sx={{ color: 'inherit' }}>
          <Menu />
        </IconButton>
        <Typography sx={{ fontWeight: 800 }}>Maandvo Dashboard</Typography>
        <HeaderProfileMenu
          initial={user?.first_name?.[0] || 'U'}
          actions={[
            { label: `Membership No: ${user?.profile_id || 'Pending'}`, onClick: () => undefined, disabled: true },
            { label: 'Dashboard', onClick: () => navigate('/my-profile') },
            { label: 'Logout', onClick: handleLogout, icon: <Logout fontSize="small" />, color: '#b71c1c' },
          ]}
        />
      </Box>

      <Box className="maandvo-dashboard-layout">
        <Box className="maandvo-dashboard-sidebar desktop-only">{sideBarContent}</Box>
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} sx={{ display: { xs: 'block', md: 'none' } }}>
          <Box sx={{ width: 280 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <IconButton onClick={() => setMobileOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            {sideBarContent}
          </Box>
        </Drawer>

        <Box className="maandvo-dashboard-main">
          {loading ? (
            <Box sx={{ minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: MAROON }} />
            </Box>
          ) : (
            <>
              <Box className="maandvo-dashboard-header">
                <Box>
                  <h1>Welcome back, {user?.first_name}!</h1>
                  <p>Find your perfect life partner with Maandvo.</p>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton aria-label="Daily match notifications" onClick={markDailyMatchNotificationsRead} sx={{ color: MAROON, border: '1px solid #ead7be', backgroundColor: '#fffaf3' }}>
                    <Badge badgeContent={unreadDailyMatchNotifications} color="error"><NotificationsOutlined /></Badge>
                  </IconButton>
                  <Box className="maandvo-dashboard-banner">
                  {profileStatus?.review_status === 'APPROVED'
                    ? 'Profile approved and live'
                    : profileStatus?.review_status === 'PENDING'
                      ? 'Profile under admin review'
                      : profileStatus?.review_status === 'CHANGES_REQUESTED'
                        ? 'Changes requested on your profile'
                        : 'સારા સંબંધોની સુંદર શરૂઆત'}
                        </Box>
                </Box>
              </Box>

              <Box className="maandvo-dashboard-stats">
                {statCards.map((stat) => (
                  <Box key={stat.label} className="maandvo-dashboard-stat">
                    {stat.icon}
                    <Box className="maandvo-dashboard-stat-value">{stat.value}</Box>
                    <Typography sx={{ color: '#7f7167', fontSize: '0.9rem' }}>{stat.label}</Typography>
                  </Box>
                ))}
              </Box>

              {profileStatus?.review_status === 'CHANGES_REQUESTED' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {profileStatus.admin_comment || 'Admin requested updates to your profile. Please complete and resubmit.'}
                </Alert>
              )}
              {firstUnreadNotification && (
                <Alert severity="success" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => handleNotificationAction(firstUnreadNotification)}>{firstUnreadNotification.link ? 'View Profile' : 'Mark read'}</Button>}>
                  {firstUnreadNotification.message || 'Your daily matches are ready.'}
                </Alert>
              )}

              <Box className="maandvo-dashboard-grid">
                <Box className="maandvo-dashboard-panel">
                  <Box className="maandvo-dashboard-panel-head">
                    <h2>Today's Matches</h2>
                    <Button variant="text" sx={{ color: MAROON, fontWeight: 700 }} onClick={() => navigate('/browse-profiles')}>
                      View All
                    </Button>
                  </Box>
                  <Box className="maandvo-dashboard-matches">
                    {recommendedProfiles.length === 0 && (
                      <Box sx={{ gridColumn: '1 / -1', py: 3, textAlign: 'center', border: '1px dashed #e5cfae', borderRadius: 2, backgroundColor: '#fffaf3' }}>
                        <Typography sx={{ color: MAROON, fontWeight: 800 }}>No daily matches are available yet.</Typography>
                        <Typography sx={{ color: '#7f7167', fontSize: '0.9rem', mt: 0.5 }}>Complete your match preferences and check after the daily matching run. New matches appear here automatically.</Typography>
                      </Box>
                    )}
                    {recommendedProfiles.slice(0, 4).map((profile) => (
                      <RotatingProfileCard
                        key={profile.id}
                        profile={profile}
                        mediaOrigin={String(api.defaults.baseURL || '').replace(/\/?api\/?$/, '')}
                        onView={() => navigate(`/profile/${profile.profile_id || profile.id}`)}
                        primaryActionLabel={profile.can_view_contact ? 'Contact Unlocked' : 'Unlock Contact'}
                        onPrimaryAction={() => navigate(`/profile/${profile.profile_id || profile.id}`)}
                        displayReligion={false}
                        compact
                      />
                    ))}
                  </Box>

                  <Box sx={{ px: 2, pb: 2 }}>
                    <Typography sx={{ color: MAROON, fontWeight: 800, mb: 1 }}>Who Viewed Your Profile</Typography>
                    {profileViewers.length === 0 ? (
                      <Typography sx={{ color: '#7f7167', fontSize: '0.9rem' }}>No profile views to show yet.</Typography>
                    ) : (
                      <Box sx={{ display: 'grid', gap: 1 }}>
                        {profileViewers.slice(0, 5).map((viewer) => (
                          <Box key={viewer.user_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, p: 1.1, border: '1px solid #f0e1cf', borderRadius: 2, backgroundColor: '#fffdfa' }}>
                            <Box>
                              <Typography sx={{ color: MAROON, fontWeight: 700, fontSize: '0.92rem' }}>{viewer.name}</Typography>
                              <Typography sx={{ color: '#7f7167', fontSize: '0.8rem' }}>{viewer.view_count} view{viewer.view_count === 1 ? '' : 's'}</Typography>
                            </Box>
                            <Button size="small" variant="outlined" onClick={() => navigate(`/profile/${viewer.profile_id || viewer.user_id}`, { state: { from: '/my-profile' } })} sx={{ color: MAROON, borderColor: MAROON }}>
                              View Profile
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ px: 2, pb: 2 }}>
                    <Typography sx={{ color: MAROON, fontWeight: 800, mb: 1 }}>Recent Account Activity</Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      {summary?.recent_spends?.slice(0, 3).map((item) => (
                        <Box
                          key={`spend-${item.id}`}
                          onClick={() => item.target_profile_id && navigate(`/profile/${item.target_profile_id}`, { state: { from: '/my-profile' } })}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                            p: 1.2,
                            border: '1px solid #f0e1cf',
                            borderRadius: 2,
                            backgroundColor: '#fffdfa',
                            cursor: item.target_profile_id ? 'pointer' : 'default',
                            '&:hover': item.target_profile_id ? { borderColor: MAROON, backgroundColor: '#fff7eb' } : undefined,
                          }}
                        >
                          <Box>
                            <Typography sx={{ color: MAROON, fontWeight: 700, fontSize: '0.92rem' }}>
                              {item.description || item.transaction_type}
                            </Typography>
                            <Typography sx={{ color: '#7f7167', fontSize: '0.8rem' }}>
                              {new Date(item.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Typography sx={{ color: MAROON, fontWeight: 800 }}>{item.amount_inr} Points</Typography>
                        </Box>
                      ))}
                      {!summary?.recent_spends?.length && !summary?.recent_recharges?.length && (
                        <Typography sx={{ color: '#7f7167', fontSize: '0.9rem' }}>
                          No recent account activity yet.
                        </Typography>
                      )}
                        {(summary?.recent_spends?.length || 0) > 0 && (
                          <Button
                            size="small"
                            sx={{ color: MAROON, fontWeight: 800, textTransform: 'none', pt: 1 }}
                            onClick={() => navigate('/wallet')}
                          >
                            View All →
                          </Button>
                        )}
                    </Box>
                  </Box>
                </Box>

                <Box className="maandvo-dashboard-side-stack">
                  <Box className="maandvo-dashboard-panel maandvo-dashboard-completion">
                    {(primaryPhoto || currentProfilePhotoUrl) && <Box component="img" src={primaryPhoto || `${mediaOrigin}${currentProfilePhotoUrl}`} alt="Primary profile" onClick={openCompleteProfile} sx={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '3px solid #f0d6ae', cursor: 'pointer', mb: 1 }} />}
                    <Typography sx={{ color: MAROON, fontWeight: 800, mb: 1.5 }}>Profile Completion</Typography>
                    <Box sx={{ px: 2 }}>
                      <LinearProgress variant="determinate" value={profileStatus?.completion_percentage || 0} sx={{ height: 10, borderRadius: 20, mb: 1.2, backgroundColor: '#f1dfc7', '& .MuiLinearProgress-bar': { backgroundColor: MAROON } }} />
                    </Box>
                    <Typography sx={{ color: MAROON, fontSize: '2rem', fontWeight: 800 }}>{profileStatus?.completion_percentage || 0}%</Typography>
                    <Typography sx={{ color: '#7f7167', fontSize: '0.92rem', mt: 0.8, mb: 1.5 }}>
                      {profileNeedsReverification ? 'Update your profile and send it for reverification' : 'Complete your profile and get better matches'}
                    </Typography>
                    <Button variant="contained" sx={{ backgroundColor: MAROON, '&:hover': { backgroundColor: '#5f000e' } }} onClick={openCompleteProfile}>
                      {profileNeedsReverification ? 'Update Profile' : 'Complete Now'}
                    </Button>
                  </Box>

                  <Box className="maandvo-dashboard-panel maandvo-dashboard-quick-actions">
                    <Typography sx={{ color: MAROON, fontWeight: 800, mb: 1 }}>Quick Actions</Typography>
                    {[
                      { label: profileNeedsReverification ? 'Update Profile' : 'Complete Profile', action: openCompleteProfile },
                      { label: 'Match Preferences', action: openMatchPreferences },
                      { label: 'Search Profiles', action: () => navigate('/browse-profiles') },
                      { label: 'Open Account', action: () => navigate('/wallet') },
                      { label: 'Open Live Profile', action: () => navigate('/my-live-profile') },
                    ].map((item) => (
                      <Box key={item.label} className="maandvo-dashboard-action-item" onClick={item.action}>
                        <span>{item.label}</span>
                        <ChevronRight fontSize="small" />
                      </Box>
                    ))}
                  </Box>

                  <Box className="maandvo-dashboard-panel maandvo-dashboard-membership">
                    <Typography sx={{ color: MAROON, fontWeight: 800, mb: 1 }}>Profile Snapshot</Typography>
                    <Typography sx={{ color: '#7f7167', fontSize: '0.9rem', mb: 0.5 }}>Review Status: {profileStatus?.review_status ?? 'INCOMPLETE'}</Typography>
                    <Typography sx={{ color: '#7f7167', fontSize: '0.9rem', mb: 0.5 }}>Profile Photos: {profileStatus?.photo_count ?? 0}</Typography>
                    <Typography sx={{ color: '#7f7167', fontSize: '0.9rem', mb: 1.2 }}>Total Spent: {summary?.total_spent ?? 0} Points</Typography>
                    <Button fullWidth variant="outlined" sx={{ borderColor: MAROON, color: MAROON }} onClick={() => navigate('/wallet')} startIcon={<CreditCard />}>
                      Open Account Details
                    </Button>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Dialog open={matchPreferenceDialogOpen} onClose={() => !matchPreferenceSaving && setMatchPreferenceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: MAROON, fontWeight: 800 }}>Match Preferences</DialogTitle>
        <DialogContent dividers>
          {matchPreferenceLoading ? (
            <Box sx={{ py: 5, display: 'grid', placeItems: 'center' }}><CircularProgress sx={{ color: MAROON }} /></Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
              <TextField label="Preferred age from" type="text" value={matchPreferences.min_age ? String(matchPreferences.min_age) : ''} onChange={(event) => { const value = event.target.value.replace(/\D/g, ''); setMatchPreferences((previous) => ({ ...previous, min_age: value ? Number(value) : 0 })); }} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 2 }} helperText="Age 18 to 80" required size="small" />
              <TextField label="Preferred age to" type="text" value={matchPreferences.max_age ? String(matchPreferences.max_age) : ''} onChange={(event) => { const value = event.target.value.replace(/\D/g, ''); setMatchPreferences((previous) => ({ ...previous, max_age: value ? Number(value) : 0 })); }} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 2 }} helperText="Age 18 to 80" required size="small" />
              <TextField select label="Preferred marital status" value={matchPreferences.marital_status} onChange={(event) => setMatchPreferences((previous) => ({ ...previous, marital_status: event.target.value }))} size="small">
                <MenuItem value="NO_PREFERENCE">No preference</MenuItem>
                {(matchPreferenceOptions.marital_status || []).filter((option) => option.toLowerCase() !== 'no preference').map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
              <TextField select label="Preferred location" value={matchPreferences.location_scope} onChange={(event) => setMatchPreferences((previous) => ({ ...previous, location_scope: event.target.value }))} size="small">
                <MenuItem value="SAME_CITY">Same city</MenuItem>
                <MenuItem value="SAME_STATE">Same state</MenuItem>
                <MenuItem value="ANYWHERE">Anywhere in India</MenuItem>
              </TextField>
              {[
                ['religion', 'Preferred religion', 'religion'],
                ['mother_tongue', 'Preferred mother tongue', 'mother_tongue'],
                ['sub_caste', 'Preferred community', 'preferred_community'],
                ['caste', 'Preferred caste', 'preferred_caste'],
                ['education', 'Preferred education', 'preferred_education'],
                ['current_country', 'Preferred current country', 'preferred_current_country'],
              ].map(([configKey, label, preferenceKey]) => (
                <TextField key={configKey} select label={label} value={matchPreferences[preferenceKey as keyof typeof matchPreferences]} onChange={(event) => setMatchPreferences((previous) => ({ ...previous, [preferenceKey]: event.target.value }))} size="small">
                  <MenuItem value="NO_PREFERENCE">No preference</MenuItem>
                  {(matchPreferenceOptions[configKey] || []).filter((option) => option.toLowerCase() !== 'no preference').map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </TextField>
              ))}
              <Typography sx={{ gridColumn: { sm: '1 / -1' }, color: '#7f7167', fontSize: '0.85rem' }}>
                These settings affect recommendations only. Saving them does not send your profile for admin reverification.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchPreferenceDialogOpen(false)} disabled={matchPreferenceSaving} sx={{ color: MAROON }}>Cancel</Button>
          <Button onClick={saveMatchPreferences} disabled={matchPreferenceLoading || matchPreferenceSaving} variant="contained" sx={{ backgroundColor: MAROON }}>
            {matchPreferenceSaving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{profileNeedsReverification ? 'Update Your Profile' : 'Complete Your Profile'}</DialogTitle>
        <DialogContent>
          {Object.keys(profileFieldConfig).length > 0 ? renderConfiguredForm() : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <Typography sx={{ gridColumn: { md: '1 / -1' }, color: MAROON, fontWeight: 800, borderBottom: '1px solid #ead7be', pb: 0.7 }}>Personal Details</Typography>
            <TextField label="Candidate Name" value={formData.first_name} required size="small" InputProps={{ readOnly: true }} helperText="Name cannot be changed after registration" />
            <TextField label="Candidate Last Name" value={formData.last_name} required size="small" InputProps={{ readOnly: true }} helperText="Name cannot be changed after registration" />
            <TextField label="Email ID" value={user?.email || ''} size="small" InputProps={{ readOnly: true }} helperText="Email cannot be changed here" />
            <TextField
              select
              label="Gender"
              value={formData.gender}
              required
              size="small"
              disabled
            >
              {(profileFieldConfig.gender?.options?.filter(Boolean) || ['MALE', 'FEMALE', 'OTHER']).map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField label="Mobile Number" value={user?.phone || ''} size="small" InputProps={{ readOnly: true }} helperText="Mobile number cannot be changed here" />
            {getFieldOptions(profileFieldConfig, 'religion', formData).isDropdown ? (
              <Autocomplete
                options={getFieldOptions(profileFieldConfig, 'religion', formData).options}
                value={formData.religion || null}
                onChange={(_e, newValue) => setFormData((prev) => ({ ...prev, religion: newValue || '', caste: '', sub_caste: '' }))}
                renderInput={(params) => <TextField {...params} label="Religion" required size="small" />}
              />
            ) : (
              <TextField label="Religion" value={formData.religion} onChange={(e) => setFormData((prev) => ({ ...prev, religion: e.target.value }))} required size="small" />
            )}
            {getFieldOptions(profileFieldConfig, 'caste', formData).isDropdown ? (
              <Autocomplete
                options={getFieldOptions(profileFieldConfig, 'caste', formData).options}
                value={formData.caste || null}
                onChange={(_e, newValue) => setFormData((prev) => ({ ...prev, caste: newValue || '', sub_caste: '' }))}
                renderInput={(params) => <TextField {...params} label="Caste" size="small" />}
              />
            ) : (
              <TextField label="Caste" value={formData.caste} onChange={(e) => setFormData((prev) => ({ ...prev, caste: e.target.value }))} size="small" />
            )}
            <TextField label="Gotra" value={formData.gotra} onChange={(e) => setFormData((prev) => ({ ...prev, gotra: e.target.value }))} required size="small" />
            <TextField label="Ekdo" value={formData.ekdo} onChange={(e) => setFormData((prev) => ({ ...prev, ekdo: e.target.value }))} size="small" />
            {getFieldOptions(profileFieldConfig, 'sub_caste', formData).isDropdown ? (
              <Autocomplete
                options={getFieldOptions(profileFieldConfig, 'sub_caste', formData).options}
                value={formData.sub_caste || null}
                onChange={(_e, newValue) => setFormData((prev) => ({ ...prev, sub_caste: newValue || '' }))}
                renderInput={(params) => <TextField {...params} label="Community" size="small" />}
              />
            ) : (
              <TextField label="Community" value={formData.sub_caste} onChange={(e) => setFormData((prev) => ({ ...prev, sub_caste: e.target.value }))} size="small" />
            )}
            {getFieldOptions(profileFieldConfig, 'mother_tongue', formData).isDropdown ? (
              <Autocomplete
                options={getFieldOptions(profileFieldConfig, 'mother_tongue', formData).options}
                value={formData.mother_tongue || null}
                onChange={(_e, newValue) => setFormData((prev) => ({ ...prev, mother_tongue: newValue || '' }))}
                renderInput={(params) => <TextField {...params} label="Mother Tongue" required size="small" />}
              />
            ) : (
              <TextField label="Mother Tongue" value={formData.mother_tongue} onChange={(e) => setFormData((prev) => ({ ...prev, mother_tongue: e.target.value }))} required size="small" />
            )}
            <TextField label="Date of Birth" type="date" value={formData.date_of_birth} onChange={(e) => setFormData((prev) => ({ ...prev, date_of_birth: e.target.value }))} required size="small" InputLabelProps={{ shrink: true }} />
            <TextField label="Time of Birth" value={formData.time_of_birth} onChange={(e) => setFormData((prev) => ({ ...prev, time_of_birth: e.target.value }))} required size="small" />
            <TextField label="Place of Birth" placeholder="Enter city or village of birth" value={formData.place_of_birth} onChange={(e) => setFormData((prev) => ({ ...prev, place_of_birth: e.target.value }))} required size="small" />
            {profileFieldConfig.height_cm?.input_type === 'height' ? (
              <Autocomplete
                options={HEIGHT_OPTIONS_CM}
                getOptionLabel={formatCmAsFeetInches}
                value={formData.height_cm || null}
                onChange={(_e, newCm) => setFormData((prev) => ({ ...prev, height_cm: newCm || '' }))}
                renderInput={(params) => <TextField {...params} label="Height" required size="small" />}
              />
            ) : (
              <TextField label="Height (cm)" value={formData.height_cm} onChange={(e) => setFormData((prev) => ({ ...prev, height_cm: e.target.value }))} required size="small" />
            )}
            <TextField select label="Marital Status" value={formData.marital_status} onChange={(e) => setFormData((prev) => ({ ...prev, marital_status: e.target.value }))} size="small">
              {getFieldOptions(profileFieldConfig, 'marital_status', formData).isDropdown ? (
                getFieldOptions(profileFieldConfig, 'marital_status', formData).options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)
              ) : (
                [
                  <MenuItem key="NEVER_MARRIED" value="NEVER_MARRIED">Never Married</MenuItem>,
                  <MenuItem key="DIVORCED" value="DIVORCED">Divorced</MenuItem>,
                  <MenuItem key="WIDOWED" value="WIDOWED">Widowed</MenuItem>,
                  <MenuItem key="SEPARATED" value="SEPARATED">Separated</MenuItem>,
                ]
              )}
            </TextField>
            <TextField select label="Are you staying with family" value={formData.staying_with_family} onChange={(e) => setFormData((prev) => ({ ...prev, staying_with_family: e.target.value }))} size="small"><MenuItem value="YES">Yes</MenuItem><MenuItem value="NO">No</MenuItem></TextField>
            <TextField label="Currently staying in country" value={formData.current_country} onChange={(e) => setFormData((prev) => ({ ...prev, current_country: e.target.value }))} size="small" />
            <Typography sx={{ gridColumn: { md: '1 / -1' }, color: MAROON, fontWeight: 800, borderBottom: '1px solid #ead7be', pb: 0.7, mt: 1 }}>Education Details</Typography>
            <TextField label="Qualification Field" value={formData.qualification_field} onChange={(e) => setFormData((prev) => ({ ...prev, qualification_field: e.target.value }))} size="small" />
            <TextField label="College Name" value={formData.college_name} onChange={(e) => setFormData((prev) => ({ ...prev, college_name: e.target.value }))} size="small" />
            <TextField label="College City" value={formData.college_city} onChange={(e) => setFormData((prev) => ({ ...prev, college_city: e.target.value }))} size="small" />
            <TextField label="University Name" value={formData.university_name} onChange={(e) => setFormData((prev) => ({ ...prev, university_name: e.target.value }))} size="small" />
            <Typography sx={{ gridColumn: { md: '1 / -1' }, color: MAROON, fontWeight: 800, borderBottom: '1px solid #ead7be', pb: 0.7, mt: 1 }}>Professional Details</Typography>
            {getFieldOptions(profileFieldConfig, 'education', formData).isDropdown ? (
              <Autocomplete
                options={getFieldOptions(profileFieldConfig, 'education', formData).options}
                value={formData.education || null}
                onChange={(_e, newValue) => setFormData((prev) => ({ ...prev, education: newValue || '' }))}
                renderInput={(params) => <TextField {...params} label="Education" required size="small" />}
              />
            ) : (
              <TextField label="Education" value={formData.education} onChange={(e) => setFormData((prev) => ({ ...prev, education: e.target.value }))} required size="small" />
            )}
            {getFieldOptions(profileFieldConfig, 'occupation', formData).isDropdown ? (
              <Autocomplete
                options={getFieldOptions(profileFieldConfig, 'occupation', formData).options}
                value={formData.occupation || null}
                onChange={(_e, newValue) => setFormData((prev) => ({ ...prev, occupation: newValue || '' }))}
                renderInput={(params) => <TextField {...params} label="Occupation" required size="small" />}
              />
            ) : (
              <TextField label="Occupation" value={formData.occupation} onChange={(e) => setFormData((prev) => ({ ...prev, occupation: e.target.value }))} required size="small" />
            )}
            <TextField label="Current Company Name" value={formData.current_company_name} onChange={(e) => setFormData((prev) => ({ ...prev, current_company_name: e.target.value }))} size="small" />
            <TextField select label="Company Type" value={formData.company_type} onChange={(e) => setFormData((prev) => ({ ...prev, company_type: e.target.value }))} size="small"><MenuItem value="PRIVATE">Private</MenuItem><MenuItem value="GOVERNMENT">Government</MenuItem><MenuItem value="SEMI_GOVERNMENT">Semi Government</MenuItem></TextField>
            <TextField label="Work Location" value={formData.work_location} onChange={(e) => setFormData((prev) => ({ ...prev, work_location: e.target.value }))} size="small" />
            <TextField select label="Work Time" value={formData.work_time} onChange={(e) => setFormData((prev) => ({ ...prev, work_time: e.target.value }))} size="small"><MenuItem value="9_TO_5">9 to 5</MenuItem><MenuItem value="SHIFTS">Shifts</MenuItem><MenuItem value="HYBRID">Hybrid</MenuItem></TextField>
            <TextField select label="Work Experience" value={formData.work_experience} onChange={(e) => setFormData((prev) => ({ ...prev, work_experience: e.target.value }))} size="small">{Array.from({ length: 16 }, (_, index) => <MenuItem key={index} value={String(index)}>{index} years</MenuItem>)}</TextField>
            <TextField select label="Job Type" value={formData.job_type} onChange={(e) => setFormData((prev) => ({ ...prev, job_type: e.target.value }))} size="small"><MenuItem value="CONTRACT">Contract</MenuItem><MenuItem value="PERMANENT">Permanent</MenuItem><MenuItem value="FREELANCING">Freelancing</MenuItem></TextField>
            <TextField label="Remark" value={formData.remark} onChange={(e) => setFormData((prev) => ({ ...prev, remark: e.target.value }))} size="small" />
            {getFieldOptions(profileFieldConfig, 'state', formData).isDropdown ? (
              <Autocomplete
                options={getFieldOptions(profileFieldConfig, 'state', formData).options}
                value={formData.state || null}
                onChange={(_e, newState) => setFormData((prev) => ({ ...prev, state: newState || '', city: '' }))}
                renderInput={(params) => <TextField {...params} label="State" required size="small" />}
              />
            ) : (
              <TextField label="State" value={formData.state} onChange={(e) => setFormData((prev) => ({ ...prev, state: e.target.value }))} required size="small" />
            )}
            {getFieldOptions(profileFieldConfig, 'city', formData).isDropdown ? (
              <Autocomplete
                options={getFieldOptions(profileFieldConfig, 'city', formData).options}
                value={formData.city || null}
                disabled={!formData.state}
                onChange={(_e, newCity) => setFormData((prev) => ({ ...prev, city: newCity || '' }))}
                renderInput={(params) => <TextField {...params} label="City" required size="small" helperText={!formData.state ? 'Select a state first' : undefined} />}
              />
            ) : (
              <TextField label="City" value={formData.city} onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))} required size="small" />
            )}
            <TextField label="Family Type" value={formData.family_type} onChange={(e) => setFormData((prev) => ({ ...prev, family_type: e.target.value }))} required size="small" />
            <TextField label="Permanent Address" value={formData.permanent_address} onChange={(e) => setFormData((prev) => ({ ...prev, permanent_address: e.target.value }))} required size="small" multiline minRows={4} sx={{ gridColumn: { md: '1 / -1' } }} />
            {getFieldOptions(profileFieldConfig, 'pin_code', formData).isDropdown ? (
              <Autocomplete
                freeSolo
                options={getFieldOptions(profileFieldConfig, 'pin_code', formData).options}
                value={formData.pin_code || ''}
                disabled={!formData.city}
                onChange={(_e, newPin) => setFormData((prev) => ({ ...prev, pin_code: newPin || '' }))}
                onInputChange={(_e, newValue) => setFormData((prev) => ({ ...prev, pin_code: newValue }))}
                renderInput={(params) => <TextField {...params} label="Pin Code" required size="small" helperText={!formData.city ? 'Select a city first' : 'Type to search, or enter manually'} />}
              />
            ) : (
              <TextField label="Pin Code" value={formData.pin_code} onChange={(e) => setFormData((prev) => ({ ...prev, pin_code: e.target.value }))} required size="small" />
            )}
            <TextField select label="Father Alive" value={formData.father_alive} onChange={(e) => setFormData((prev) => ({ ...prev, father_alive: e.target.value }))} size="small">
              {getFieldOptions(profileFieldConfig, 'father_alive', formData).isDropdown ? (
                getFieldOptions(profileFieldConfig, 'father_alive', formData).options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)
              ) : (
                [<MenuItem key="YES" value="YES">Yes</MenuItem>, <MenuItem key="NO" value="NO">No</MenuItem>]
              )}
            </TextField>
            <TextField select label="Mother Alive" value={formData.mother_alive} onChange={(e) => setFormData((prev) => ({ ...prev, mother_alive: e.target.value }))} size="small">
              {getFieldOptions(profileFieldConfig, 'mother_alive', formData).isDropdown ? (
                getFieldOptions(profileFieldConfig, 'mother_alive', formData).options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)
              ) : (
                [<MenuItem key="YES" value="YES">Yes</MenuItem>, <MenuItem key="NO" value="NO">No</MenuItem>]
              )}
            </TextField>
            <TextField label="Father Mobile Number" value={formData.father_mobile} onChange={(e) => setFormData((prev) => ({ ...prev, father_mobile: e.target.value }))} size="small" />
            <TextField label="Mother Mobile Number" value={formData.mother_mobile} onChange={(e) => setFormData((prev) => ({ ...prev, mother_mobile: e.target.value }))} size="small" />
            <TextField select label="Siblings" value={formData.has_siblings} onChange={(e) => setFormData((prev) => ({ ...prev, has_siblings: e.target.value, siblings_count: e.target.value === 'YES' ? prev.siblings_count : '' }))} size="small">
              {getFieldOptions(profileFieldConfig, 'has_siblings', formData).isDropdown ? (
                getFieldOptions(profileFieldConfig, 'has_siblings', formData).options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)
              ) : (
                [<MenuItem key="YES" value="YES">Yes</MenuItem>, <MenuItem key="NO" value="NO">No</MenuItem>]
              )}
            </TextField>
            {formData.has_siblings === 'YES' && (
              <TextField label="Siblings Count" type="number" value={formData.siblings_count} onChange={(e) => setFormData((prev) => ({ ...prev, siblings_count: e.target.value }))} inputProps={{ min: 1 }} required size="small" />
            )}
            <TextField label="About Me" multiline minRows={3} value={formData.about_me} onChange={(e) => setFormData((prev) => ({ ...prev, about_me: e.target.value }))} required sx={{ gridColumn: { md: '1 / -1' } }} />
            <Typography sx={{ gridColumn: { md: '1 / -1' }, color: MAROON, fontWeight: 800, borderBottom: '1px solid #ead7be', pb: 0.7, mt: 1 }}>Hobby Details</Typography>
            <TextField label="Hobbies" placeholder="Add hobbies separated by commas" value={formData.hobbies} onChange={(e) => setFormData((prev) => ({ ...prev, hobbies: e.target.value }))} size="small" sx={{ gridColumn: { md: '1 / -1' } }} />
            <Typography sx={{ gridColumn: { md: '1 / -1' }, color: MAROON, fontWeight: 800, borderBottom: '1px solid #ead7be', pb: 0.7, mt: 1 }}>Family Details</Typography>
            <TextField select label="Brother(s) excluding you" value={formData.brother_count} onChange={(e) => setFormData((prev) => ({ ...prev, brother_count: e.target.value }))} size="small"><MenuItem value="0">No</MenuItem><MenuItem value="1">1</MenuItem><MenuItem value="2">2</MenuItem><MenuItem value="3">3</MenuItem><MenuItem value="4">4+</MenuItem></TextField>
            <TextField select label="Brother marriage status" value={formData.brother_marriage_status} onChange={(e) => setFormData((prev) => ({ ...prev, brother_marriage_status: e.target.value }))} size="small"><MenuItem value="ONE_MARRIED">One married</MenuItem><MenuItem value="ONE_UNMARRIED">One unmarried</MenuItem><MenuItem value="ALL_MARRIED">All married</MenuItem><MenuItem value="ALL_UNMARRIED">All unmarried</MenuItem></TextField>
            <TextField select label="Sister(s) excluding you" value={formData.sister_count} onChange={(e) => setFormData((prev) => ({ ...prev, sister_count: e.target.value }))} size="small"><MenuItem value="0">No</MenuItem><MenuItem value="1">1</MenuItem><MenuItem value="2">2</MenuItem><MenuItem value="3">3</MenuItem><MenuItem value="4">4+</MenuItem></TextField>
            <TextField select label="Sister marriage status" value={formData.sister_marriage_status} onChange={(e) => setFormData((prev) => ({ ...prev, sister_marriage_status: e.target.value }))} size="small"><MenuItem value="ONE_MARRIED">One married</MenuItem><MenuItem value="ONE_UNMARRIED">One unmarried</MenuItem><MenuItem value="ALL_MARRIED">All married</MenuItem><MenuItem value="ALL_UNMARRIED">All unmarried</MenuItem></TextField>
            <TextField label="Grandfather Name" value={formData.grandfather_name} onChange={(e) => setFormData((prev) => ({ ...prev, grandfather_name: e.target.value }))} size="small" />
            <TextField label="Grandmother Name" value={formData.grandmother_name} onChange={(e) => setFormData((prev) => ({ ...prev, grandmother_name: e.target.value }))} size="small" />
            <TextField label="Native Place" value={formData.native_place} onChange={(e) => setFormData((prev) => ({ ...prev, native_place: e.target.value }))} size="small" />
            <Typography sx={{ gridColumn: { md: '1 / -1' }, color: MAROON, fontWeight: 800, borderBottom: '1px solid #ead7be', pb: 0.7, mt: 1 }}>Address Details</Typography>
            <TextField label="Permanent Country" value={formData.permanent_country} onChange={(e) => setFormData((prev) => ({ ...prev, permanent_country: e.target.value }))} size="small" />
            <TextField select label="Permanent Property Type" value={formData.permanent_property_type} onChange={(e) => setFormData((prev) => ({ ...prev, permanent_property_type: e.target.value }))} size="small"><MenuItem value="OWNED">Owned</MenuItem><MenuItem value="RENTED">Rented</MenuItem></TextField>
            <TextField label="Permanent Property Details" value={formData.permanent_property_details} onChange={(e) => setFormData((prev) => ({ ...prev, permanent_property_details: e.target.value }))} size="small" />
            <TextField label="Candidate Current Address" value={formData.current_address} onChange={(e) => setFormData((prev) => ({ ...prev, current_address: e.target.value }))} size="small" multiline minRows={4} sx={{ gridColumn: { md: '1 / -1' } }} />
            <TextField label="Current Country" value={formData.current_country_address} onChange={(e) => setFormData((prev) => ({ ...prev, current_country_address: e.target.value }))} size="small" />
            <TextField label="Current State" value={formData.current_state} onChange={(e) => setFormData((prev) => ({ ...prev, current_state: e.target.value }))} size="small" />
            <TextField label="Current City" value={formData.current_city} onChange={(e) => setFormData((prev) => ({ ...prev, current_city: e.target.value }))} size="small" />
            <TextField label="Current Pin Code" value={formData.current_pin_code} onChange={(e) => setFormData((prev) => ({ ...prev, current_pin_code: e.target.value }))} size="small" />
            <TextField select label="Current Property Type" value={formData.current_property_type} onChange={(e) => setFormData((prev) => ({ ...prev, current_property_type: e.target.value }))} size="small"><MenuItem value="OWNED">Owned</MenuItem><MenuItem value="RENTED">Rented</MenuItem></TextField>
            <TextField label="Current Property Details" value={formData.current_property_details} onChange={(e) => setFormData((prev) => ({ ...prev, current_property_details: e.target.value }))} size="small" />
            <Box sx={{ gridColumn: { md: '1 / -1' } }}>
              <Typography sx={{ color: MAROON, fontWeight: 800, mb: 0.5 }}>Profile Photos</Typography>
                <Typography sx={{ color: '#7f7167', fontSize: '0.85rem', mb: 1.2 }}>The profile photo is used as your main photo across the platform. You may add up to 3 additional gallery photos.</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
                <Box>
                  <Button component="label" variant="contained" fullWidth sx={{ backgroundColor: MAROON, '&:hover': { backgroundColor: '#5f000e' } }}>
                    {primaryPhoto || currentProfilePhotoUrl ? 'Replace Profile Photo' : 'Upload Profile Photo'}
                    <input hidden type="file" accept="image/*" onChange={handlePrimaryPhotoUpload} />
                  </Button>
                  {(primaryPhoto || currentProfilePhotoUrl) && <Box component="img" src={primaryPhoto || `${mediaOrigin}${currentProfilePhotoUrl}`} alt="Primary profile preview" onClick={() => setImagePreview({ src: primaryPhoto || `${mediaOrigin}${currentProfilePhotoUrl}`, label: 'Profile Photo' })} sx={{ mt: 1, width: 112, height: 112, objectFit: 'cover', borderRadius: 1, border: '2px solid #ead1b0', cursor: 'zoom-in' }} />}
                </Box>
                <Box>
                  <Button component="label" variant="outlined" fullWidth disabled={existingGalleryPhotos.length + additionalPhotos.length >= 3} sx={{ borderColor: MAROON, color: MAROON }}>
                    Add Gallery Photo ({existingGalleryPhotos.length + additionalPhotos.length}/3)
                    <input hidden type="file" accept="image/*" onChange={handleAdditionalPhotoUpload} />
                  </Button>
                  {(existingGalleryPhotos.length > 0 || additionalPhotos.length > 0) && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1 }}>
                    {existingGalleryPhotos.map((photo, index) => <Box key={`existing-dialog-${photo}`} sx={{ position: 'relative' }}><Box component="img" src={photo} alt={`Gallery photo ${index + 1}`} onClick={() => setImagePreview({ src: photo, label: `Gallery Photo ${index + 1}` })} sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid #e6d4be', cursor: 'zoom-in' }} /></Box>)}
                    {additionalPhotos.map((photo, index) => <Box key={`${photo}-${index}`} sx={{ position: 'relative' }}><Box component="img" src={photo} alt={`New gallery preview ${existingGalleryPhotos.length + index + 1}`} onClick={() => setImagePreview({ src: photo, label: `New Gallery Photo ${index + 1}` })} sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid #e6d4be', cursor: 'zoom-in' }} /><Button size="small" onClick={() => setAdditionalPhotos((previous) => previous.filter((_, photoIndex) => photoIndex !== index))} sx={{ position: 'absolute', top: -10, right: -10, minWidth: 24, width: 24, height: 24, p: 0, borderRadius: '50%', backgroundColor: '#7b0011', color: 'white', '&:hover': { backgroundColor: '#5f000e' } }}>×</Button></Box>)}
                  </Box>}
                </Box>
              </Box>
            </Box>
          </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveProfileAndContinueLater} variant="outlined" sx={{ borderColor: MAROON, color: MAROON }} disabled={profileSubmitting}>
            Save and Continue Later
          </Button>
          <Button onClick={submitCompleteProfile} variant="contained" sx={{ backgroundColor: MAROON }} disabled={profileSubmitting}>
            {profileSubmitting ? 'Submitting...' : profileNeedsReverification ? 'Submit for Reverification' : 'Submit for Review'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(imagePreview)} onClose={() => setImagePreview(null)} maxWidth="md" fullWidth PaperProps={{ sx: { width: 'min(860px, calc(100vw - 32px))', height: 'min(680px, calc(100vh - 80px))' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{imagePreview?.label}<IconButton aria-label="Close image preview" onClick={() => setImagePreview(null)}><Close /></IconButton></DialogTitle>
        <DialogContent sx={{ display: 'grid', placeItems: 'center', overflow: 'hidden', p: 1 }}>{imagePreview && <Box component="img" src={imagePreview.src} alt={imagePreview.label} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}</DialogContent>
      </Dialog>

    </Box>
  );
};

export default DashboardPage;
